import { SerializedComponent } from "../../components/SerializedApp";
import { makeText } from "../builder-utils";
import {
    ActionComponentDescription,
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    TableGlideType,
    getPrimitiveColumns,
    isStringTypeKind,
} from "../description";
import { makeTextPropertyDescriptor } from "./descriptor-utils";
import { ComponentHandlerBase } from "./handler";
import { ColumnPropertyFlag, ColumnPropertyHandler, SwitchPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { ComponentBuilder, ComponentConfiguratorContext, ComponentDescriptor } from ".";

// This is a clone of the "Local Weather" component.
// If you find significant discrepancies, then either:
// The requirements for one of them have changed, meaning this comment should be removed,
// OR a change made to one wasn't made to the other.

const ComponentKindLocalTime: ComponentKind = "local-time";

const locationPropertyHandler = new ColumnPropertyHandler(
    "location",
    "Column",
    [ColumnPropertyFlag.Required],
    undefined,
    ["location", "coordinates"],
    getPrimitiveColumns,
    "string",
    PropertySection.Data
);

const hideCityPropertyHandler = new SwitchPropertyHandler({ hideCity: false }, "Hide Location", PropertySection.Design);

interface LocalTimeComponentDescription extends ActionComponentDescription {
    readonly location: PropertyDescription;
    readonly hideCity: PropertyDescription;
}

export class LocalTimeComponentHandler extends ComponentHandlerBase<LocalTimeComponentDescription> {
    constructor() {
        super(ComponentKindLocalTime);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: LocalTimeComponentDescription,
        tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        _outputContext: SerializedComponent,
        _searchVariable: SerializedComponent | undefined,
        _searchNotActive: SerializedComponent | undefined,
        uniqueID: string
    ): SerializedComponent {
        const { b } = builder;

        const locationProperty = locationPropertyHandler.getColumnName(desc);
        if (locationProperty === undefined) return b.emptyUI;

        const location = makeText(builder, desc.location, tables.input);
        if (location === undefined) return b.emptyUI;

        const hideCity = hideCityPropertyHandler.getSwitch(desc);

        // FIXME: Add support for Actions

        return b.ui("LocalTime", {
            location: b.lookupGeoAddress(b.getProperty(locationProperty, b.getContext, tables.input), uniqueID),
            hideCity: b.constant(hideCity),
        });
    }

    public getDescriptor(
        _desc: LocalTimeComponentDescription,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<LocalTimeComponentDescription> {
        return {
            name: "Local Time",
            description: "Show the local time at the given location.",
            img: "componentLocalTime",
            group: "Charts",
            // FIXME: Create a help page, and enter the URL for it here
            helpUrl: "",
            properties: [
                makeTextPropertyDescriptor<LocalTimeComponentDescription>(
                    "location",
                    "Location",
                    "Enter a location",
                    true,
                    mutatingScreenKind,
                    {
                        getAllowedColumns: t => t.columns.filter(c => isStringTypeKind(c.type.kind)),
                        preferredNames: ["location", "address"],
                        columnFirst: true,
                    }
                ),
                hideCityPropertyHandler,
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }
}
